/**
 * KYC Pages Management Component
 *
 * Pages are stored in Firestore at: config/kyc_configuration/kyc_pages
 */

import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconCopy,
  IconEdit,
  IconEye,
  IconFileText,
  IconRefresh,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFirestore } from "../../helpers/firebaseContext";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  setDoc,
} from "firebase/firestore";
import { modals } from "@mantine/modals";
import { showNotification } from "@mantine/notifications";
import DOMPurify from "dompurify";
import type {
  ComponentOption,
  ComponentSection,
  KYCPage,
  PageAction,
  SectionField,
} from "../../types";

const FIRESTORE_PATHS = {
  CONFIG: "config",
  KYC_CONFIG: "kyc_configuration",
  KYC_PAGES: "kyc_pages",
} as const;

const NOTIFICATIONS = {
  LOAD_ERROR: {
    color: "red",
    title: "Error",
    message: "Failed to load KYC pages. Please try again.",
    autoClose: 5000,
  },
  DELETE_SUCCESS: (title: string) => ({
    color: "teal",
    title: "Success",
    message: `Page "${title}" deleted successfully`,
    autoClose: 3000,
  }),
  DELETE_ERROR: {
    color: "red",
    title: "Error",
    message: "Failed to delete page. Please try again.",
    autoClose: 5000,
  },
  DUPLICATE_SUCCESS: {
    color: "teal",
    title: "Success",
    message: "Page duplicated successfully",
    autoClose: 3000,
  },
  DUPLICATE_ERROR: {
    color: "red",
    title: "Error",
    message: "Failed to duplicate page. Please try again.",
    autoClose: 5000,
  },
  INVALID_ID: {
    color: "red",
    title: "Error",
    message: "Page ID cannot be empty",
    autoClose: 5000,
  },
  DUPLICATE_ID: (id: string) => ({
    color: "red",
    title: "Error",
    message: `A page with ID "${id}" already exists`,
    autoClose: 5000,
  }),
} as const;

const LIST_STYLES = {
  metric: {
    border: "1px solid #e9ecef",
    borderRadius: 8,
    padding: "12px 14px",
    background: "#fff",
  },
  mono: {
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  },
  emptyCell: {
    padding: "48px 16px",
    textAlign: "center" as const,
  },
} as const;

const PREVIEW_STYLES = {
  shell: {
    maxWidth: 620,
    margin: "0 auto",
    border: "1px solid #dee2e6",
    borderRadius: 16,
    background: "#f8f9fa",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "min(760px, calc(100vh - 170px))",
    minHeight: 520,
  },
  topBar: {
    background: "#228be6",
    color: "#fff",
    padding: "14px 18px",
  },
  body: {
    padding: 16,
    flex: 1,
    overflowY: "auto",
  },
  section: {
    background: "#fff",
    border: "1px solid #e9ecef",
    borderRadius: 12,
    padding: 16,
  },
  fieldShell: {
    paddingTop: 2,
  },
  fieldLabel: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginBottom: 7,
  },
  inputBox: {
    minHeight: 42,
    border: "1px solid #ced4da",
    borderRadius: 8,
    background: "#f8f9fa",
    padding: "10px 12px",
    color: "#868e96",
  },
  dashedBox: {
    minHeight: 76,
    border: "1px dashed #adb5bd",
    borderRadius: 10,
    background: "#f8f9fa",
    padding: 14,
  },
  optionBox: {
    border: "1px solid #e9ecef",
    borderRadius: 10,
    padding: "9px 11px",
    background: "#fff",
  },
  nestedBox: {
    marginTop: 10,
    marginLeft: 18,
    paddingLeft: 12,
    borderLeft: "2px solid #dee2e6",
  },
  actions: {
    borderTop: "1px solid #e9ecef",
    padding: 16,
    background: "#fff",
    flexShrink: 0,
  },
} as const;

type PageMetrics = {
  pages: number;
  fields: number;
  components: number;
  actions: number;
};

type VisibilityStats = {
  totalSections: number;
  appSections: number;
  pdfSections: number;
  totalFields: number;
  appFields: number;
  pdfFields: number;
};

export type KYCPreviewLanguage = "en" | "ar";

export type KYCPreviewNavigation = {
  current: number;
  total: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
};

const isArabicPreview = (language: KYCPreviewLanguage): boolean =>
  language === "ar";

const getPreviewDirection = (language: KYCPreviewLanguage): "rtl" | "ltr" =>
  isArabicPreview(language) ? "rtl" : "ltr";

const getLocalizedText = (
  englishValue: string | undefined,
  arabicValue: string | undefined,
  language: KYCPreviewLanguage,
  fallback: string,
): string => {
  const preferred = isArabicPreview(language) ? arabicValue : englishValue;
  const secondary = isArabicPreview(language) ? englishValue : arabicValue;

  return preferred?.trim() || secondary?.trim() || fallback;
};

const previewCopy = (
  language: KYCPreviewLanguage,
  english: string,
  arabic: string,
): string => (isArabicPreview(language) ? arabic : english);

const getPageTitle = (
  page: KYCPage,
  language: KYCPreviewLanguage = "en",
): string => getLocalizedText(page.title, page.title_ar, language, page.id);

const getPageDescription = (
  page: KYCPage,
  language: KYCPreviewLanguage = "en",
): string => {
  return getLocalizedText(
    page.description,
    page.description_ar,
    language,
    previewCopy(language, "No description provided", "لا يوجد وصف"),
  );
};

const sortPagesByTitle = (pages: KYCPage[]): KYCPage[] => {
  return [...pages].sort((a, b) =>
    getPageTitle(a).localeCompare(getPageTitle(b)),
  );
};

const normalizeSearchValue = (value: string): string =>
  value.trim().toLowerCase();

const getErrorMessage = (error: unknown): string => {
  return error instanceof Error ? error.message : "Unknown error occurred";
};

const generateDuplicateId = (baseId: string): string => {
  const timestamp = Date.now();
  return `${baseId}_copy_${timestamp}`;
};

const isUniquePageId = (id: string, pages: KYCPage[]): boolean => {
  return !pages.some((p) => p.id === id);
};

const getCurrentRoutePath = (pathname: string, search: string, hash: string): string =>
  `${pathname}${search}${hash}`;

const buildPageEditPath = (pageId: string, returnTo: string): string =>
  `/kyc_configuration/add_page/${encodeURIComponent(pageId)}?returnTo=${encodeURIComponent(returnTo)}`;

const countPageFields = (page: KYCPage): number => {
  if (!page.components || !Array.isArray(page.components)) {
    return 0;
  }

  return page.components.reduce((total, component) => {
    const fieldsCount = component.section_fields?.length || 0;
    return total + fieldsCount;
  }, 0);
};

const collectFieldsWithNestedOptions = (
  fields: SectionField[] = [],
): SectionField[] => {
  return fields.flatMap((field) => [
    field,
    ...(field.options || []).flatMap((option) =>
      collectFieldsWithNestedOptions(option.section_fields || []),
    ),
  ]);
};

const getPageVisibilityStats = (page: KYCPage): VisibilityStats => {
  const sections = page.components || [];
  const fields = sections.flatMap((section) =>
    collectFieldsWithNestedOptions(section.section_fields || []),
  );

  return {
    totalSections: sections.length,
    appSections: sections.filter((section) =>
      isKycVisible(section.is_visible_kyc),
    ).length,
    pdfSections: sections.filter((section) =>
      isKycVisible(section.is_visible_contract),
    ).length,
    totalFields: fields.length,
    appFields: fields.filter((field) => isKycVisible(field.is_visible_kyc))
      .length,
    pdfFields: fields.filter((field) => isKycVisible(field.is_visible_contract))
      .length,
  };
};

const createDuplicatedPage = (page: KYCPage, newId: string): KYCPage => ({
  ...page,
  id: newId,
  title: `${page.title} (Copy)`,
  title_ar: `${page.title_ar} (نسخة)`,
});

const pageMatchesSearch = (page: KYCPage, search: string): boolean => {
  const query = normalizeSearchValue(search);

  if (!query) {
    return true;
  }

  const searchText = [
    page.id,
    page.title,
    page.title_ar,
    page.description,
    page.description_ar,
    page.page_condition,
    page.eTag,
    ...(page.tags || []),
    page.is_skip_kyc ? "skip skippable" : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return searchText.includes(query);
};

const formatRevision = (eTag?: string): string => {
  if (!eTag) {
    return "No revision";
  }

  const parsed = new Date(eTag);

  if (Number.isNaN(parsed.getTime())) {
    return eTag;
  }

  return parsed.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const SummaryMetric = ({
  label,
  value,
}: {
  label: string;
  value: number | string;
}) => (
  <div style={LIST_STYLES.metric}>
    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
      {label}
    </Text>
    <Text size="xl" fw={800}>
      {value}
    </Text>
  </div>
);

const VisibilitySummary = ({ page }: { page: KYCPage }) => {
  const stats = getPageVisibilityStats(page);
  const hasAppVisibility = stats.appSections > 0 || stats.appFields > 0;
  const hasPdfVisibility = stats.pdfSections > 0 || stats.pdfFields > 0;
  const visibilityMode = (() => {
    if (hasAppVisibility && hasPdfVisibility) {
      return { label: "Mixed", color: "violet" };
    }

    if (hasAppVisibility) {
      return { label: "App only", color: "blue" };
    }

    if (hasPdfVisibility) {
      return { label: "PDF only", color: "teal" };
    }

    return { label: "Hidden", color: "gray" };
  })();

  return (
    <Stack gap={5}>
      <Badge
        size="xs"
        color={visibilityMode.color}
        variant="light"
        style={{ alignSelf: "flex-start" }}
      >
        {visibilityMode.label}
      </Badge>

      <Group gap={6} wrap="nowrap">
        <Badge size="sm" variant="light" color="blue" style={{ width: 62 }}>
          App
        </Badge>
        <Text size="sm" fw={600}>
          {stats.appFields}/{stats.totalFields}
        </Text>
        <Text size="xs" c="dimmed">
          fields
        </Text>
        <Text size="xs" c="dimmed">
          {stats.appSections}/{stats.totalSections} sections
        </Text>
      </Group>

      <Group gap={6} wrap="nowrap">
        <Badge size="sm" variant="light" color="teal" style={{ width: 62 }}>
          PDF
        </Badge>
        <Text size="sm" fw={600}>
          {stats.pdfFields}/{stats.totalFields}
        </Text>
        <Text size="xs" c="dimmed">
          fields
        </Text>
        <Text size="xs" c="dimmed">
          {stats.pdfSections}/{stats.totalSections} sections
        </Text>
      </Group>
    </Stack>
  );
};

export const isKycVisible = (visible?: boolean): boolean => visible !== false;

const normalizeFieldType = (type?: string): string =>
  String(type || "")
    .replace(/[\s_-]/g, "")
    .toLowerCase();

const stripHtml = (value?: string): string =>
  (value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getFieldLabel = (
  field: SectionField,
  language: KYCPreviewLanguage,
): string =>
  getLocalizedText(
    field.label,
    field.label_ar,
    language,
    field.key || previewCopy(language, "Untitled field", "حقل بدون عنوان"),
  );

const getFieldPlaceholder = (
  field: SectionField,
  language: KYCPreviewLanguage,
): string => getLocalizedText(field.placeholder, field.placeholder_ar, language, "");

const getFieldValue = (
  field: SectionField,
  language: KYCPreviewLanguage,
): string => {
  const value = isArabicPreview(language)
    ? field.value_ar || field.value
    : field.value || field.value_ar;

  return Array.isArray(value) ? value.join(", ") : String(value || "");
};

const getOptionLabel = (
  option: ComponentOption,
  language: KYCPreviewLanguage,
): string =>
  getLocalizedText(
    option.label,
    option.label_ar,
    language,
    option.value || previewCopy(language, "Option", "خيار"),
  );

const getOptionPlaceholder = (
  option: ComponentOption,
  language: KYCPreviewLanguage,
): string =>
  getLocalizedText(option.placeholder, option.placeholder_ar, language, "");

const getActionLabel = (
  action: PageAction,
  language: KYCPreviewLanguage,
): string =>
  getLocalizedText(
    action.label,
    action.label_ar,
    language,
    action.key || action.type || previewCopy(language, "Action", "إجراء"),
  );

const SafeHtmlPreview = ({
  html,
  language,
}: {
  html?: string;
  language: KYCPreviewLanguage;
}) => {
  if (!html?.trim()) {
    return null;
  }

  return (
    <Text
      size="sm"
      c="dimmed"
      component="div"
      dir={getPreviewDirection(language)}
      style={{
        lineHeight: 1.55,
        textAlign: isArabicPreview(language) ? "right" : "left",
      }}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  );
};

const PreviewFieldLabel = ({
  field,
  language,
}: {
  field: SectionField;
  language: KYCPreviewLanguage;
}) => (
  <div
    style={{
      ...PREVIEW_STYLES.fieldLabel,
      justifyContent: isArabicPreview(language) ? "flex-end" : "flex-start",
    }}
  >
    <Text size="sm" fw={600}>
      {getFieldLabel(field, language)}
    </Text>
    {field.required && (
      <Text size="sm" c="red" fw={700}>
        *
      </Text>
    )}
    {(isArabicPreview(language) ? field.tooltip_ar || field.tooltip : field.tooltip) && (
      <Badge size="xs" variant="light" color="gray">
        {previewCopy(language, "Help", "مساعدة")}
      </Badge>
    )}
  </div>
);

const PreviewFormDataBinding = ({
  field,
  language,
}: {
  field: SectionField;
  language: KYCPreviewLanguage;
}) => {
  const formDataKey = field.key?.trim();

  if (!formDataKey) {
    return null;
  }

  return (
    <Group
      mb={7}
      justify={isArabicPreview(language) ? "flex-end" : "flex-start"}
    >
      <Text
        size="xs"
        c="dimmed"
        dir="ltr"
        style={{
          ...LIST_STYLES.mono,
          overflowWrap: "anywhere",
          textAlign: "left",
        }}
      >
        {formDataKey}
      </Text>
    </Group>
  );
};

const PreviewInputBox = ({
  value,
  language,
}: {
  value?: string;
  language: KYCPreviewLanguage;
}) => (
  <div style={PREVIEW_STYLES.inputBox}>
    <Text size="sm" c="dimmed" ta={isArabicPreview(language) ? "right" : "left"}>
      {value || previewCopy(language, "Customer input", "إدخال العميل")}
    </Text>
  </div>
);

const PreviewNestedFields = ({
  fields,
  label,
  language,
  depth = 0,
}: {
  fields?: SectionField[];
  label: string;
  language: KYCPreviewLanguage;
  depth?: number;
}) => {
  const visibleFields = (fields || []).filter((field) =>
    isKycVisible(field.is_visible_kyc),
  );

  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <div style={PREVIEW_STYLES.nestedBox}>
      <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb="xs">
        {label}
      </Text>
      <Stack gap="sm">
        {visibleFields.map((field, index) => (
          <PreviewField
            key={`${field.key || field.label || "nested"}-${index}`}
            field={field}
            language={language}
            depth={depth + 1}
          />
        ))}
      </Stack>
    </div>
  );
};

const PreviewOptionList = ({
  field,
  mode,
  depth,
  language,
}: {
  field: SectionField;
  mode: "radio" | "checkbox";
  depth: number;
  language: KYCPreviewLanguage;
}) => {
  const options = field.options || [];

  if (options.length === 0) {
    return (
      <PreviewInputBox
        value={previewCopy(
          language,
          "No options configured",
          "لا توجد خيارات مهيأة",
        )}
        language={language}
      />
    );
  }

  return (
    <Stack gap="xs">
      {options.map((option, index) => {
        const placeholder = getOptionPlaceholder(option, language);

        return (
          <div
            key={`${field.key}-${option.value || option.label || index}`}
            style={PREVIEW_STYLES.optionBox}
          >
            <Group gap="xs" align="flex-start" wrap="nowrap">
              <Text
                size="lg"
                c="blue"
                lh={1.1}
                style={{ width: 20, textAlign: "center" }}
              >
                {mode === "radio" ? "○" : "□"}
              </Text>
              <div style={{ flex: 1 }}>
                <Text
                  size="sm"
                  fw={500}
                  ta={isArabicPreview(language) ? "right" : "left"}
                >
                  {getOptionLabel(option, language)}
                </Text>
                {option.value && (
                  <Text size="xs" c="dimmed" style={LIST_STYLES.mono}>
                    {option.value}
                  </Text>
                )}
                {placeholder && (
                  <div style={{ marginTop: 6 }}>
                    <SafeHtmlPreview html={placeholder} language={language} />
                  </div>
                )}
              </div>
            </Group>
            <PreviewNestedFields
              fields={option.section_fields}
              label={
                mode === "radio"
                  ? previewCopy(
                      language,
                      "Shown when selected",
                      "يظهر عند الاختيار",
                    )
                  : previewCopy(
                      language,
                      "Shown when checked",
                      "يظهر عند التحديد",
                    )
              }
              language={language}
              depth={depth}
            />
          </div>
        );
      })}
    </Stack>
  );
};

const PreviewField = ({
  field,
  language,
  depth = 0,
}: {
  field: SectionField;
  language: KYCPreviewLanguage;
  depth?: number;
}) => {
  if (!isKycVisible(field.is_visible_kyc)) {
    return null;
  }

  const type = normalizeFieldType(field.type);
  const placeholderText =
    stripHtml(getFieldPlaceholder(field, language)) ||
    getFieldValue(field, language) ||
    undefined;

  const content = (() => {
    switch (type) {
      case "text":
        return (
          <Stack gap={5}>
            {getFieldPlaceholder(field, language) ? (
              <SafeHtmlPreview
                html={getFieldPlaceholder(field, language)}
                language={language}
              />
            ) : (
              <Text size="sm" c="dimmed">
                {previewCopy(language, "Display text", "نص للعرض")}
              </Text>
            )}
          </Stack>
        );
      case "input":
      case "email":
      case "number":
      case "textarea":
        return <PreviewInputBox value={placeholderText} language={language} />;
      case "datepicker":
        return (
          <PreviewInputBox
            value={previewCopy(language, "Select date", "اختر التاريخ")}
            language={language}
          />
        );
      case "countrypicker":
        return (
          <PreviewInputBox
            value={previewCopy(language, "Select country", "اختر الدولة")}
            language={language}
          />
        );
      case "paci":
        return (
          <div style={PREVIEW_STYLES.dashedBox}>
            <Text size="sm" fw={600}>
              {previewCopy(language, "PACI authentication", "مصادقة هويتي")}
            </Text>
            <Text size="xs" c="dimmed">
              {previewCopy(
                language,
                "The mobile app opens the PACI verification flow here.",
                "يفتح تطبيق الهاتف مسار التحقق من هويتي هنا.",
              )}
            </Text>
          </div>
        );
      case "radio":
      case "select":
        return (
          <PreviewOptionList
            field={field}
            mode="radio"
            depth={depth}
            language={language}
          />
        );
      case "checkbox":
        return (
          <PreviewOptionList
            field={field}
            mode="checkbox"
            depth={depth}
            language={language}
          />
        );
      case "fileupload":
        return (
          <div style={PREVIEW_STYLES.dashedBox}>
            <Text size="sm" fw={600}>
              {previewCopy(language, "File upload", "تحميل ملف")}
            </Text>
            <Text size="xs" c="dimmed">
              {field.filePrompt ||
                [
                  field.maxFile ? `Max ${field.maxFile} files` : null,
                  field.fileSize ? `${field.fileSize} KB` : null,
                  field.fileExtension,
                ]
                  .filter(Boolean)
                  .join(" · ") ||
                previewCopy(
                  language,
                  "Customer selects supporting documents here.",
                  "يختار العميل المستندات الداعمة هنا.",
                )}
            </Text>
            {field.fileAIChecker && field.fileAIChecker.length > 0 && (
              <Group gap={4} mt="xs">
                {field.fileAIChecker.map((checker) => (
                  <Badge key={`${field.key}-${checker}`} size="xs" color="gray">
                    {checker}
                  </Badge>
                ))}
              </Group>
            )}
          </div>
        );
      case "signature":
        return (
          <div style={{ ...PREVIEW_STYLES.dashedBox, minHeight: 120 }}>
            <Text size="sm" c="dimmed" ta="center" mt={30}>
              {previewCopy(language, "Signature pad", "مساحة التوقيع")}
            </Text>
          </div>
        );
      case "agreementcheckbox":
        return (
          <div style={PREVIEW_STYLES.optionBox}>
            <Group gap="xs" align="flex-start" wrap="nowrap">
              <Text
                size="lg"
                c="blue"
                lh={1.1}
                style={{ width: 20, textAlign: "center" }}
              >
                □
              </Text>
              <div style={{ flex: 1 }}>
                <Text size="sm" ta={isArabicPreview(language) ? "right" : "left"}>
                  {getFieldLabel(field, language)}
                </Text>
                {getFieldPlaceholder(field, language) && (
                  <div style={{ marginTop: 6 }}>
                    <SafeHtmlPreview
                      html={getFieldPlaceholder(field, language)}
                      language={language}
                    />
                  </div>
                )}
                {field.agreementType && (
                  <Badge size="xs" variant="light" color="gray" mt="xs">
                    {field.agreementType}
                  </Badge>
                )}
              </div>
            </Group>
          </div>
        );
      case "thankyoutickmark":
        return (
          <Stack align="center" gap="xs">
            <Text c="blue" lh={1} style={{ fontSize: 44 }}>
              ✓
            </Text>
            {getFieldPlaceholder(field, language) && (
              <SafeHtmlPreview
                html={getFieldPlaceholder(field, language)}
                language={language}
              />
            )}
          </Stack>
        );
      case "transferamount":
        return (
          <PreviewInputBox
            value={previewCopy(language, "Transfer amount", "مبلغ التحويل")}
            language={language}
          />
        );
      case "downloadcontract":
        return (
          <Button variant="light" fullWidth disabled>
            {getFieldLabel(field, language) ||
              previewCopy(language, "Download contract", "تحميل العقد")}
          </Button>
        );
      default:
        return (
          <Stack gap={5}>
            <PreviewInputBox
              value={
                placeholderText ||
                previewCopy(
                  language,
                  `Unsupported field: ${field.type}`,
                  `حقل غير مدعوم: ${field.type}`,
                )
              }
              language={language}
            />
            {field.options && field.options.length > 0 && (
              <PreviewOptionList
                field={field}
                mode="radio"
                depth={depth}
                language={language}
              />
            )}
          </Stack>
        );
    }
  })();

  const hideRepeatedLabel = type === "agreementcheckbox";

  return (
    <div style={PREVIEW_STYLES.fieldShell}>
      {!hideRepeatedLabel && (
        <PreviewFieldLabel field={field} language={language} />
      )}
      <PreviewFormDataBinding field={field} language={language} />
      {field.condition && (
        <Text size="xs" c="dimmed" mb={6} style={LIST_STYLES.mono}>
          Condition: {field.condition}
        </Text>
      )}
      {content}
    </div>
  );
};

const PreviewSection = ({
  section,
  language,
}: {
  section: ComponentSection;
  language: KYCPreviewLanguage;
}) => {
  if (!isKycVisible(section.is_visible_kyc)) {
    return null;
  }

  const fields = (section.section_fields || []).filter((field) =>
    isKycVisible(field.is_visible_kyc),
  );
  const showTitle = section.is_visible_title !== false;
  const title = getLocalizedText(
    section.section_title,
    section.section_title_ar,
    language,
    "",
  );
  const description = getLocalizedText(
    section.section_description,
    section.section_description_ar,
    language,
    "",
  );

  return (
    <Paper withBorder radius="md" p="md" style={PREVIEW_STYLES.section}>
      <Stack gap="md">
        {(showTitle || section.list_view || section.remove_item) && (
          <div>
            {showTitle && title && (
              <Text fw={700} ta="center" dir={getPreviewDirection(language)}>
                {title}
              </Text>
            )}
            <Group gap={6} justify="center" mt={title ? 6 : 0}>
              {section.section_id && (
                <Badge size="xs" variant="light" color="gray">
                  {section.section_id}
                </Badge>
              )}
              {section.list_view && (
                <Badge size="xs" variant="light" color="blue">
                  {previewCopy(language, "Add row", "إضافة صف")}
                </Badge>
              )}
              {section.remove_item && (
                <Badge size="xs" variant="light" color="red">
                  {previewCopy(language, "Removable", "قابل للحذف")}
                </Badge>
              )}
            </Group>
          </div>
        )}

        {showTitle && description && (
          <Text
            size="sm"
            c="dimmed"
            dir={getPreviewDirection(language)}
            ta={isArabicPreview(language) ? "right" : "left"}
          >
            {description}
          </Text>
        )}

        {section.condition && (
          <Text size="xs" c="dimmed" style={LIST_STYLES.mono}>
            Section condition: {section.condition}
          </Text>
        )}

        {fields.length === 0 ? (
          <Text
            size="sm"
            c="dimmed"
            ta={isArabicPreview(language) ? "right" : "left"}
          >
            {previewCopy(
              language,
              "No visible KYC fields configured in this section.",
              "لا توجد حقول KYC ظاهرة مهيأة في هذا القسم.",
            )}
          </Text>
        ) : (
          <Stack gap="md">
            {fields.map((field, index) => (
              <PreviewField
                key={`${section.section_id || "section"}-${field.key || index}`}
                field={field}
                language={language}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

const PreviewActions = ({
  actions,
  navigation,
  language,
}: {
  actions?: PageAction[];
  navigation?: KYCPreviewNavigation;
  language: KYCPreviewLanguage;
}) => {
  if (navigation) {
    return (
      <div style={PREVIEW_STYLES.actions}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            gap: 10,
          }}
        >
          <Button
            variant="default"
            disabled={!navigation.hasPrevious}
            onClick={navigation.onPrevious}
          >
            {previewCopy(language, "Back", "السابق")}
          </Button>
          <Text size="xs" c="dimmed" ta="center" style={LIST_STYLES.mono}>
            {navigation.current}/{navigation.total}
          </Text>
          <Button disabled={!navigation.hasNext} onClick={navigation.onNext}>
            {previewCopy(language, "Next", "التالي")}
          </Button>
        </div>
      </div>
    );
  }

  const visibleActions = (actions || []).filter(
    (action) => action.visible !== false,
  );
  const actionsToShow =
    visibleActions.length > 0
      ? visibleActions
      : [
          {
            key: "next",
            label: "Next",
            label_ar: "التالي",
            type: "next",
            is_save: true,
            visible: true,
          },
        ];

  return (
    <div style={PREVIEW_STYLES.actions}>
      <Group grow gap="sm">
        {actionsToShow.map((action) => {
          const isPrevious = action.key?.toLowerCase() === "previous";
          return (
            <Button
              key={`${action.key}-${action.label}`}
              variant={isPrevious ? "default" : "filled"}
              disabled
            >
              {getActionLabel(action, language)}
            </Button>
          );
        })}
      </Group>
    </div>
  );
};

export const KYCPagePreview = ({
  page,
  showIntro = true,
  navigation,
  language = "en",
  scrollRatio,
  onScrollRatioChange,
  shellStyle,
}: {
  page: KYCPage;
  showIntro?: boolean;
  navigation?: KYCPreviewNavigation;
  language?: KYCPreviewLanguage;
  scrollRatio?: number;
  onScrollRatioChange?: (scrollRatio: number) => void;
  shellStyle?: React.CSSProperties;
}) => {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const isSyncingScrollRef = useRef(false);
  const visibleSections = (page.components || []).filter((section) =>
    isKycVisible(section.is_visible_kyc),
  );
  const isSpecialPage =
    page.id === "kyc_requirements" ||
    page.id === "paci_authentication" ||
    page.id === "docusign_signing";
  const pageDescription = getLocalizedText(
    page.description,
    page.description_ar,
    language,
    "",
  );

  useEffect(() => {
    if (bodyRef.current) {
      const maxScrollTop =
        bodyRef.current.scrollHeight - bodyRef.current.clientHeight;
      const nextScrollTop =
        maxScrollTop > 0 ? maxScrollTop * (scrollRatio ?? 0) : 0;

      if (Math.abs(bodyRef.current.scrollTop - nextScrollTop) > 1) {
        isSyncingScrollRef.current = true;
        bodyRef.current.scrollTop = nextScrollTop;
        window.requestAnimationFrame(() => {
          isSyncingScrollRef.current = false;
        });
      }
    }
  }, [page.id, scrollRatio]);

  const handlePreviewScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (isSyncingScrollRef.current) {
        return;
      }

      const maxScrollTop =
        event.currentTarget.scrollHeight - event.currentTarget.clientHeight;
      onScrollRatioChange?.(
        maxScrollTop > 0 ? event.currentTarget.scrollTop / maxScrollTop : 0,
      );
    },
    [onScrollRatioChange],
  );

  return (
    <Stack gap="md">
      {showIntro && (
        <Text size="sm" c="dimmed">
          {previewCopy(
            language,
            "Rough mobile preview based on the Flutter contract app's KYC section and field renderers.",
            "معاينة تقريبية للهاتف بناء على عرض KYC في تطبيق العقود.",
          )}
        </Text>
      )}

      <div
        style={{ ...PREVIEW_STYLES.shell, ...shellStyle }}
        dir={getPreviewDirection(language)}
      >
        <div style={PREVIEW_STYLES.topBar}>
          <Text fw={700}>KFH Capital KYC</Text>
          <Text size="xs" style={{ opacity: 0.85 }}>
            {getPageTitle(page, language)}
          </Text>
        </div>

        <div ref={bodyRef} style={PREVIEW_STYLES.body} onScroll={handlePreviewScroll}>
          <Stack gap="md">
            <Paper withBorder radius="md" p="md" bg="white">
              <Group justify="space-between" align="flex-start" gap="md">
                <div style={{ flex: 1 }}>
                  <Text fw={700}>{getPageTitle(page, language)}</Text>
                  {pageDescription && (
                    <Text
                      size="sm"
                      c="dimmed"
                      mt={4}
                      ta={isArabicPreview(language) ? "right" : "left"}
                    >
                      {pageDescription}
                    </Text>
                  )}
                </div>
                <Badge variant="light" color="gray" style={LIST_STYLES.mono}>
                  {page.id}
                </Badge>
              </Group>
              {isSpecialPage && (
                <Badge variant="light" color="blue" mt="sm">
                  {previewCopy(
                    language,
                    "Special Flutter screen",
                    "شاشة خاصة في Flutter",
                  )}
                </Badge>
              )}
            </Paper>

            {visibleSections.length === 0 ? (
              <Paper withBorder radius="md" p="md" bg="white">
                <Text size="sm" c="dimmed">
                  {previewCopy(
                    language,
                    "No visible KYC sections configured for this page.",
                    "لا توجد أقسام KYC ظاهرة مهيأة لهذه الصفحة.",
                  )}
                </Text>
              </Paper>
            ) : (
              visibleSections.map((section, index) => (
                <PreviewSection
                  key={`${section.section_id || section.section_title || "section"}-${index}`}
                  section={section}
                  language={language}
                />
              ))
            )}
          </Stack>
        </div>

        <PreviewActions
          actions={page.action}
          navigation={navigation}
          language={language}
        />
      </div>
    </Stack>
  );
};

const useKYCPages = (db: any) => {
  const [pages, setPages] = useState<KYCPage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadPages = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const querySnapshot = await getDocs(
        collection(
          db,
          FIRESTORE_PATHS.CONFIG,
          FIRESTORE_PATHS.KYC_CONFIG,
          FIRESTORE_PATHS.KYC_PAGES,
        ),
      );

      const pagesData: KYCPage[] = querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as KYCPage,
      );

      setPages(sortPagesByTitle(pagesData));
    } catch (error) {
      console.error("Error loading pages:", error);
      setError(getErrorMessage(error));
      showNotification(NOTIFICATIONS.LOAD_ERROR);
    } finally {
      setIsLoading(false);
    }
  }, [db]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  return { pages, isLoading, error, reloadPages: loadPages };
};

const usePageOperations = (
  db: any,
  pages: KYCPage[],
  reloadPages: () => Promise<void>,
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnToPath = useMemo(
    () => getCurrentRoutePath(location.pathname, location.search, location.hash),
    [location.hash, location.pathname, location.search],
  );

  const handleDelete = useCallback(
    (pageId: string, pageTitle: string): void => {
      modals.openConfirmModal({
        title: "Delete KYC Page",
        centered: true,
        children: (
          <Text size="sm">
            Are you sure you want to delete <strong>"{pageTitle}"</strong>? This
            action cannot be undone.
          </Text>
        ),
        labels: { confirm: "Delete", cancel: "Cancel" },
        confirmProps: { color: "red" },
        onConfirm: async () => {
          try {
            await deleteDoc(
              doc(
                db,
                FIRESTORE_PATHS.CONFIG,
                FIRESTORE_PATHS.KYC_CONFIG,
                FIRESTORE_PATHS.KYC_PAGES,
                pageId,
              ),
            );

            showNotification(NOTIFICATIONS.DELETE_SUCCESS(pageTitle));
            await reloadPages();
          } catch (error) {
            console.error("Error deleting page:", error);
            showNotification(NOTIFICATIONS.DELETE_ERROR);
          }
        },
      });
    },
    [db, reloadPages],
  );

  const handleDuplicate = useCallback(
    (page: KYCPage): void => {
      const defaultNewId = generateDuplicateId(page.id);

      modals.openConfirmModal({
        title: "Duplicate KYC Page",
        centered: true,
        children: (
          <Stack gap="md">
            <Text size="sm">
              You are about to duplicate <strong>"{page.title}"</strong>
            </Text>
            <div>
              <Text size="sm" fw={500} mb="xs">
                New Page ID:
              </Text>
              <input
                id="duplicate-page-id"
                type="text"
                defaultValue={defaultNewId}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontFamily: "monospace",
                }}
              />
              <Text size="xs" c="dimmed" mt="xs">
                Enter a unique ID for the duplicated page
              </Text>
            </div>
          </Stack>
        ),
        labels: { confirm: "Duplicate", cancel: "Cancel" },
        confirmProps: { color: "blue" },
        onConfirm: async () => {
          try {
            const inputElement = document.getElementById(
              "duplicate-page-id",
            ) as HTMLInputElement;
            const newPageId = inputElement?.value.trim() || defaultNewId;

            if (!newPageId) {
              showNotification(NOTIFICATIONS.INVALID_ID);
              return;
            }

            if (!isUniquePageId(newPageId, pages)) {
              showNotification(NOTIFICATIONS.DUPLICATE_ID(newPageId));
              return;
            }

            const duplicatedPage = createDuplicatedPage(page, newPageId);

            await setDoc(
              doc(
                db,
                FIRESTORE_PATHS.CONFIG,
                FIRESTORE_PATHS.KYC_CONFIG,
                FIRESTORE_PATHS.KYC_PAGES,
                duplicatedPage.id,
              ),
              duplicatedPage,
            );

            showNotification(NOTIFICATIONS.DUPLICATE_SUCCESS);
            await reloadPages();
          } catch (error) {
            console.error("Error duplicating page:", error);
            showNotification(NOTIFICATIONS.DUPLICATE_ERROR);
          }
        },
      });
    },
    [db, reloadPages, pages],
  );

  const handlePreview = useCallback((page: KYCPage): void => {
    modals.open({
      title: `Preview: ${getPageTitle(page)}`,
      size: "80rem",
      children: <KYCPagePreview page={page} />,
    });
  }, []);

  const handleEdit = useCallback(
    (pageId: string): void => {
      navigate(buildPageEditPath(pageId, returnToPath));
    },
    [navigate, returnToPath],
  );

  return { handleDelete, handleDuplicate, handlePreview, handleEdit };
};

const KYCPages: React.FC = () => {
  const db = useFirestore();
  const [search, setSearch] = useState("");
  const { pages, isLoading, error, reloadPages } = useKYCPages(db);
  const handlers = usePageOperations(db, pages, reloadPages);

  const metrics = useMemo<PageMetrics>(() => {
    return pages.reduce(
      (totals, page) => ({
        pages: totals.pages + 1,
        fields: totals.fields + countPageFields(page),
        components: totals.components + (page.components?.length || 0),
        actions: totals.actions + (page.action?.length || 0),
      }),
      { pages: 0, fields: 0, components: 0, actions: 0 },
    );
  }, [pages]);

  const filteredPages = useMemo(
    () => pages.filter((page) => pageMatchesSearch(page, search)),
    [pages, search],
  );

  if (isLoading) {
    return (
      <main style={{ padding: "1rem 0" }}>
        <Group justify="center" mt="xl">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading KYC pages...</Text>
          </Stack>
        </Group>
      </main>
    );
  }

  if (error) {
    return (
      <main style={{ padding: "1rem 0" }}>
        <Card withBorder p="xl" style={{ textAlign: "center" }}>
          <Text c="red" size="lg" fw={600} mb="md">
            Error Loading Pages
          </Text>
          <Text c="dimmed" mb="lg">
            {error}
          </Text>
          <Button onClick={reloadPages}>Retry</Button>
        </Card>
      </main>
    );
  }

  if (pages.length === 0) {
    return (
      <main style={{ padding: "1rem 0" }}>
        <Card withBorder p="xl" style={{ textAlign: "center" }}>
          <Stack align="center" gap="md">
            <IconFileText size={44} style={{ opacity: 0.35 }} />
            <div>
              <Text size="lg" fw={600} mb="xs">
                No KYC pages found
              </Text>
              <Text c="dimmed">
                Create the first reusable page from the Add KYC Page action.
              </Text>
            </div>
          </Stack>
        </Card>
      </main>
    );
  }

  return (
    <main style={{ padding: "1rem 0" }}>
      <Stack gap="md">
        <Paper withBorder p="md" radius="md">
          <Group justify="space-between" align="flex-end" gap="md">
            <div>
              <Text size="lg" fw={700}>
                Pages
              </Text>
              <Text size="sm" c="dimmed">
                Showing {filteredPages.length} of {pages.length}
              </Text>
            </div>

            <Group
              gap="xs"
              wrap="nowrap"
              style={{ flex: "1 1 420px", justifyContent: "flex-end" }}
            >
              <TextInput
                aria-label="Search KYC pages"
                placeholder="Search pages, IDs, tags, conditions"
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                style={{ width: "min(520px, 100%)" }}
              />
              <Tooltip label="Reload pages">
                <ActionIcon
                  variant="light"
                  size="lg"
                  aria-label="Reload pages"
                  onClick={() => void reloadPages()}
                >
                  <IconRefresh size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>

          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm" mt="md">
            <SummaryMetric label="Pages" value={metrics.pages} />
            <SummaryMetric label="Fields" value={metrics.fields} />
            <SummaryMetric label="Components" value={metrics.components} />
            <SummaryMetric label="Actions" value={metrics.actions} />
          </SimpleGrid>
        </Paper>

        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table.ScrollContainer minWidth={1080}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead bg="gray.0">
                <Table.Tr>
                  <Table.Th>Page</Table.Th>
                  <Table.Th>Structure</Table.Th>
                  <Table.Th>Visibility</Table.Th>
                  <Table.Th>Revision</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredPages.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5} style={LIST_STYLES.emptyCell}>
                      <Stack align="center" gap="sm">
                        <Text fw={600}>No pages match this search</Text>
                        <Button variant="subtle" onClick={() => setSearch("")}>
                          Clear search
                        </Button>
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  filteredPages.map((page) => {
                    return (
                      <Table.Tr key={page.id}>
                        <Table.Td style={{ minWidth: 330 }}>
                          <Stack gap={5}>
                            <Text fw={700} lineClamp={1}>
                              {getPageTitle(page)}
                            </Text>
                            <Badge
                              variant="light"
                              color="gray"
                              size="sm"
                              style={{
                                ...LIST_STYLES.mono,
                                alignSelf: "flex-start",
                              }}
                            >
                              {page.id}
                            </Badge>
                            <Text size="sm" c="dimmed" lineClamp={2}>
                              {getPageDescription(page)}
                            </Text>
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Badge variant="light" color="blue">
                              {countPageFields(page)} fields
                            </Badge>
                            <Badge variant="light" color="teal">
                              {page.components?.length || 0} sections
                            </Badge>
                            <Badge variant="light" color="grape">
                              {page.action?.length || 0} actions
                            </Badge>
                          </Group>
                        </Table.Td>
                        <Table.Td style={{ minWidth: 250 }}>
                          <VisibilitySummary page={page} />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" style={LIST_STYLES.mono}>
                            {formatRevision(page.eTag)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} justify="flex-end" wrap="nowrap">
                            <Tooltip label="Preview page">
                              <ActionIcon
                                variant="subtle"
                                color="cyan"
                                aria-label={`Preview ${getPageTitle(page)}`}
                                onClick={() => handlers.handlePreview(page)}
                              >
                                <IconEye size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Edit page">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                aria-label={`Edit ${getPageTitle(page)}`}
                                onClick={() => handlers.handleEdit(page.id)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Duplicate page">
                              <ActionIcon
                                variant="subtle"
                                color="yellow"
                                aria-label={`Duplicate ${getPageTitle(page)}`}
                                onClick={() => handlers.handleDuplicate(page)}
                              >
                                <IconCopy size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete page">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                aria-label={`Delete ${getPageTitle(page)}`}
                                onClick={() =>
                                  handlers.handleDelete(
                                    page.id,
                                    getPageTitle(page),
                                  )
                                }
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      </Stack>
    </main>
  );
};

export default KYCPages;
